import { useEffect, useState } from "react";
import {
  CalendarClock,
  Download,
  Lock,
  RefreshCw,
  Info,
  History,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import HrKpiExportPanel from "@/components/HrKpiExportPanel";

function fmtDateTime(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

/**
 * HR Export (Monthly JSON).
 *
 * One file per completed calendar month, for upload into the HR system.
 * A month opens on the 2nd of the following month (data entry lags a day)
 * and then stays open for ever. Files are rebuilt live on each download.
 */
export default function HrExport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyPeriod, setBusyPeriod] = useState(null);
  const [error, setError] = useState("");
  const [audit, setAudit] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/hr-export/months", { params: { limit: 12 } });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load months");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const loadAudit = async () => {
    try {
      const r = await api.get("/hr-export-audit/log", { params: { limit: 25 } });
      setAudit(r.data.entries || []);
    } catch (e) {
      toast.error("Could not load the download log");
    }
  };

  const download = async (m) => {
    setBusyPeriod(m.period);
    try {
      const r = await api.get(`/hr-export/${m.period}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download =
        (r.headers?.["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] ||
        m.filename ||
        `monthly-${m.period}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${m.label} exported`);
      if (audit) loadAudit();
    } catch (e) {
      // The blob responseType means an error body arrives as a Blob — read it.
      let detail = "";
      const body = e?.response?.data;
      if (body instanceof Blob) {
        try {
          detail = JSON.parse(await body.text())?.detail || "";
        } catch {
          detail = "";
        }
      } else {
        detail = body?.detail || "";
      }
      toast.error(detail || "Export failed");
    } finally {
      setBusyPeriod(null);
    }
  };

  const months = data?.months || [];

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="hr-export-page">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarClock size={22} className="text-orange-600" /> HR Exports
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Two different files. Use <strong>HR KPI Export</strong> for the HR KPI Center;
            the full data dump below is a separate archive.
          </p>
        </div>
        <button
          onClick={load}
          className="px-3 py-2 border border-slate-300 hover:bg-slate-50 text-sm rounded-md flex items-center gap-1.5 shrink-0"
          data-testid="hr-export-refresh"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* The KPI Center file comes FIRST: the raw dump below was being downloaded by
          mistake and rejected by HR with
          'Expected format starting with "hr-kpi-monthly/"'. */}
      <HrKpiExportPanel />

      <div className="mt-10 mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
        <strong>Below is NOT the KPI Center file.</strong> It is the full monthly data
        archive (<code className="rounded bg-amber-100 px-1">aampl-monthly-hr-export</code>,
        ~3&nbsp;MB of raw records). The HR KPI Center rejects it. Use it only for backup
        or audit hand-off.
      </div>

      {/* Cutoff rule explainer */}
      <div
        className="bg-sky-50 border border-sky-200 rounded-lg p-4 mb-6 flex gap-3"
        data-testid="hr-export-rule"
      >
        <Info size={18} className="text-sky-600 shrink-0 mt-0.5" />
        <div className="text-sm text-slate-700 space-y-1.5">
          <p>
            <strong>How the monthly cutoff works.</strong> Each file covers the{" "}
            <strong>1st to the last day of that month</strong> (28, 29, 30 or 31 days —
            whatever the month has), in{" "}
            <strong>IST ({data?.timezone || "Asia/Kolkata"})</strong>. Nothing dated outside
            the month is ever included.
          </p>
          <p>
            A month becomes downloadable on the{" "}
            <strong>2nd of the following month</strong> — the last day's entries are typed
            up on the next working day — and then stays downloadable for good. The current
            month and future months can never be exported.
          </p>
          <p>
            The file is <strong>rebuilt from the live database on every download</strong>,
            so corrections entered later for dates inside the month are picked up. The date
            window never widens. Master data (customers, materials, operations, settings)
            has no date dimension and is included in full, separately, as a live snapshot at
            the moment of download.
          </p>
          {data?.today_ist && (
            <p className="text-xs text-slate-500">
              Today in IST: <strong>{data.today_ist}</strong>
            </p>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3 mb-4">
          {error}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 text-sm">Last 12 completed months</h3>
          <span className="text-xs text-slate-400">Newest first</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[620px] text-sm" data-testid="hr-export-months-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left font-medium px-5 py-2.5">Month</th>
                <th className="text-left font-medium px-5 py-2.5">Year</th>
                <th className="text-left font-medium px-5 py-2.5">Days</th>
                <th className="text-left font-medium px-5 py-2.5">Period</th>
                <th className="text-right font-medium px-5 py-2.5">Export</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-slate-400 text-xs">
                    Loading…
                  </td>
                </tr>
              ) : months.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-slate-400 text-xs">
                    No completed months yet.
                  </td>
                </tr>
              ) : (
                months.map((m) => (
                  <tr
                    key={m.period}
                    className={m.available ? "" : "bg-slate-50/60"}
                    data-testid={`hr-export-row-${m.period}`}
                  >
                    <td className="px-5 py-3 font-medium text-slate-900">{m.month_name}</td>
                    <td className="px-5 py-3 text-slate-600">{m.year}</td>
                    <td className="px-5 py-3 text-slate-600">{m.days_in_month}</td>
                    <td className="px-5 py-3 text-slate-500 text-xs">
                      {m.period_start} → {m.period_end}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {m.available ? (
                        <button
                          onClick={() => download(m)}
                          disabled={busyPeriod === m.period}
                          className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white text-xs rounded-md inline-flex items-center gap-1.5"
                          data-testid={`hr-export-download-${m.period}`}
                          title={`Download ${m.filename}`}
                        >
                          <Download size={14} />
                          {busyPeriod === m.period ? "Preparing…" : "Download"}
                        </button>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1.5 text-xs text-slate-400 cursor-not-allowed"
                          title={m.unavailable_reason || ""}
                          data-testid={`hr-export-locked-${m.period}`}
                        >
                          <Lock size={13} />
                          Available from {m.available_from_label}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Download log */}
      <div className="mt-6">
        {audit === null ? (
          <button
            onClick={loadAudit}
            className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1.5"
            data-testid="hr-export-audit-show"
          >
            <History size={14} /> Show download log
          </button>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900 text-sm flex items-center gap-1.5">
                <History size={15} /> Download log
              </h3>
              <button
                onClick={() => setAudit(null)}
                className="text-xs text-slate-400 hover:text-slate-700"
              >
                Hide
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-sm" data-testid="hr-export-audit-table">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left font-medium px-5 py-2.5">Month</th>
                    <th className="text-left font-medium px-5 py-2.5">Downloaded by</th>
                    <th className="text-left font-medium px-5 py-2.5">When</th>
                    <th className="text-right font-medium px-5 py-2.5">Records</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {audit.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="p-5 text-center text-slate-400 text-xs">
                        No downloads yet.
                      </td>
                    </tr>
                  ) : (
                    audit.map((a) => (
                      <tr key={a.audit_id}>
                        <td className="px-5 py-2.5 font-medium text-slate-800">{a.period}</td>
                        <td className="px-5 py-2.5 text-slate-600">
                          {a.downloaded_by_name || a.downloaded_by_email || "—"}
                        </td>
                        <td className="px-5 py-2.5 text-slate-500 text-xs">
                          {fmtDateTime(a.downloaded_at)}
                        </td>
                        <td className="px-5 py-2.5 text-right text-slate-600">
                          {a.record_counts?.all_records ?? "—"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
